import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import debounce from 'lodash/debounce';
import striptags from 'striptags';
import { v4 } from 'uuid';

import Accordion from '../../components/Accordion';
import PageTitle from '../../components/PageTitle';
import Grid from '../../components/Grid';
import Header from '../../components/Header';
import Button from '../../components/Button';
import Item from '../../components/Item';
import Icon from '../../components/Icon';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt';
import Loader from '../../components/Loader';

import messages from './messages';
import './styles.css';
import Divider from '../../components/Divider';
import ScholarshipsInfo from './scholarshipsInfo';
import ScholarshipForm from './scholarshipForm';
import SubHeader from './subHeader';
import { defaultScholarship } from './constants';
import { isUrlValid } from '../../utils/url';
import GenericModal from '../../components/GenericModal';
import {
  checkRequiredFields,
  fixNumericFields,
  requiredValuesExist,
  checkForErrors,
  errorsExist,
} from './utils';

class Scholarships extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.shape({
      scholarships: PropTypes.shape({
        url: PropTypes.string,
        addInfo: PropTypes.string,
        publishStatus: PropTypes.bool,
        meritBasedScholarships: PropTypes.array,
      }),
    }),
    saveScholarshipsMutation: PropTypes.func,
    intl: PropTypes.object,
  };

  static defaultConfig = {
    index: '',
    formOpen: false,
    formDirty: false,
    errors: { requiredFieldsMissing: [] },
    forceShowErrors: false,
    isNew: false,
    mbScholarship: null,
  };

  static getDerivedStateFromProps(props, state) {
    const { stateInitialized } = state;
    if (!stateInitialized && get(props, 'viewer')) {
      const {
        scholarshipsConfig,
        scholarshipsConfigKeys,
      } = Scholarships.getScholarshipsConfig(props);
      return {
        ...state,
        stateInitialized: true,
        url: get(props, 'viewer.scholarships.url', '') || '',
        addInfo: get(props, 'viewer.scholarships.addInfo', '') || '',
        scholarshipsConfig,
        scholarshipsConfigKeys,
        initialState: {
          scholarshipsConfig: cloneDeep(scholarshipsConfig),
        },
        currentFocusIndex: null,
      };
    }
    return state;
  }

  static getScholarshipsConfig = (props) => {
    const scholarships = get(props, 'viewer.scholarships.meritBasedScholarships', []);
    const scholarshipsConfig = {};
    const scholarshipsConfigKeys = [];
    scholarships.forEach((s) => {
      const index = v4();
      scholarshipsConfig[index] = {
        ...Scholarships.defaultConfig,
        index,
        mbScholarship: omit(s, '__typename'),
      };
      scholarshipsConfigKeys.push(index);
    });
    return { scholarshipsConfig, scholarshipsConfigKeys };
  };

  constructor(props) {
    super(props);
    const {
      scholarshipsConfig,
      scholarshipsConfigKeys,
    } = Scholarships.getScholarshipsConfig(props);
    this.state = {
      invalidUrl: false,
      saveFailureProps: null,
      stateInitialized: false,
      url: get(props, 'viewer.scholarships.url', '') || '',
      addInfo: get(props, 'viewer.scholarships.addInfo', '') || '',
      scholarshipsConfig,
      scholarshipsConfigKeys,
      initialState: {
        scholarshipsConfig: cloneDeep(scholarshipsConfig),
      },
    };
    this.debounceSaveScholarships = debounce(this.saveScholarship, 400);
  }

  setConfigState = (index, configState) => {
    const { scholarshipsConfig } = this.state;
    this.setState({
      scholarshipsConfig: {
        ...scholarshipsConfig,
        [index]: {
          ...scholarshipsConfig[index],
          ...configState,
        },
      },
    });
  };

  handleAddScholarship = () => {
    const index = v4();
    this.setConfigState(index, {
      ...Scholarships.defaultConfig,
      index,
      isNew: true,
      formOpen: true,
      mbScholarship: defaultScholarship,
    });
    const { scholarshipsConfigKeys } = this.state;
    this.setState({
      scholarshipsConfigKeys: scholarshipsConfigKeys.concat([index]),
      currentFocusIndex: index,
    });
  };

  onSaveAllScholarshipsSuccess = () => {
    const { scholarshipsConfig } = this.state;
    const changedConfig = {};
    Object.keys(scholarshipsConfig).forEach((key) => {
      changedConfig[key] = {
        ...Scholarships.defaultConfig,
        index: key,
        mbScholarship: fixNumericFields(cloneDeep(scholarshipsConfig[key].mbScholarship)),
      };
    });
    this.setState({
      initialState: {
        scholarshipsConfig: changedConfig,
      },
    });
  };

  onSaveSingleScholarshipSuccess = (index, isDelete) => {
    const { scholarshipsConfig: initialConfig } = this.state.initialState;
    const { scholarshipsConfig } = this.state;

    const savingScholarsipConfig = scholarshipsConfig[index];
    const savedScholarshipConfig = {
      ...Scholarships.defaultConfig,
      index: savingScholarsipConfig.index,
      mbScholarship: fixNumericFields(savingScholarsipConfig.mbScholarship),
    };

    if (isDelete) {
      // Just deleted one scholarship
      const changedInitialConfig = {};
      Object.keys(initialConfig).forEach((key) => {
        if (key !== index) {
          changedInitialConfig[key] = {
            ...initialConfig[key],
          };
        }
      });
      const changedConfig = {};
      Object.keys(scholarshipsConfig).forEach((key) => {
        if (key !== index) {
          changedConfig[key] = {
            ...scholarshipsConfig[key],
          };
        }
      });
      this.setState({
        scholarshipsConfig: {
          ...changedConfig,
        },
        initialState: {
          scholarshipsConfig: changedInitialConfig,
        },
      });
    } else {
      // Saved one scholarship which was newly created or modified
      initialConfig[index] = {
        ...savedScholarshipConfig,
      };
      this.setState({
        saveFailureProps: null,
        scholarshipsConfig: {
          ...scholarshipsConfig,
          [index]: savedScholarshipConfig,
        },
        initialState: {
          scholarshipsConfig: initialConfig,
        },
      });
    }
  };

  onSaveScholarshipSuccess = (index, isDelete, saveAll) => {
    this.setState({
      saveFailureProps: null,
    });
    if (!index && !saveAll) {
      // Just saved the Website URL and additional info
      return true;
    }

    if (saveAll) {
      // Just saved all the scholarships modified
      this.onSaveAllScholarshipsSuccess();
    } else {
      // Saved/Deleted just one scholarship
      this.onSaveSingleScholarshipSuccess(index, isDelete);
    }
    return true;
  };

  onSaveScholarshipFailure = (error, index, isDelete, saveAll, name) => {
    console.log('Error saving Scholarships', error); // eslint-disable-line no-console
    this.setState({
      saveFailureProps: {
        index,
        isDelete,
        saveAll,
        name,
      },
    });
    return false;
  };

  saveScholarship = (index, isDelete, saveAll) => {
    const { saveScholarshipsMutation } = this.props;
    let { url, addInfo } = this.state;
    addInfo = striptags(addInfo);
    url = striptags(url);

    const { scholarshipsConfig: initialConfig } = this.state.initialState;
    const { scholarshipsConfig } = this.state;
    const savingScholarsipConfig = scholarshipsConfig[index];
    let scholarshipsToSave = [];
    if (!index && !saveAll && !isDelete) {
      // Only website url and additional infor to be saved
      scholarshipsToSave = Object.keys(initialConfig).map((key) => initialConfig[key].mbScholarship);
    } else if (isDelete) {
      scholarshipsToSave = Object.keys(initialConfig)
        .filter((key) => key !== index)
        .map((key) => initialConfig[key].mbScholarship);
    } else if (saveAll === true) {
      scholarshipsToSave = Object.keys(scholarshipsConfig).map((key) => scholarshipsConfig[key].mbScholarship);
    } else if (savingScholarsipConfig.isNew) {
      scholarshipsToSave =
        Object.keys(initialConfig)
          .map((key) => initialConfig[key].mbScholarship)
          .concat(savingScholarsipConfig.mbScholarship);
    } else {
      scholarshipsToSave = Object.keys(initialConfig).map((key) => {
        if (key === index) {
          return savingScholarsipConfig.mbScholarship;
        }
        return initialConfig[key].mbScholarship;
      });
    }

    const variables = {
      input: {
        scholarship: {
          url,
          addInfo,
          meritBasedScholarships: scholarshipsToSave.map((s) => fixNumericFields(s)),
        },
      },
    };

    /* istanbul ignore next */
    const scholarshipName = savingScholarsipConfig ? savingScholarsipConfig.mbScholarship.name : '';
    return saveScholarshipsMutation({
      variables,
    })
      .then(() => this.onSaveScholarshipSuccess(index, isDelete, saveAll))
      .catch((err) => this.onSaveScholarshipFailure(err, index, isDelete, saveAll, scholarshipName));
  };

  onToggleForm = (index) => {
    const { scholarshipsConfig } = this.state;
    /* istanbul ignore else */
    if (scholarshipsConfig[index]) {
      this.setConfigState(index, {
        formOpen: !scholarshipsConfig[index].formOpen,
      });
      if (!scholarshipsConfig[index].formOpen) {
        this.setState({ currentFocusIndex: index });
      }
    }
  };

  onScholarshipInfoChange = (e, name, changed) => {
    let invalidUrl = false;
    let { url, addInfo } = { ...this.state, [name]: e.target.value };
    if (changed) {
      addInfo = striptags(addInfo);
      url = striptags(url);
      invalidUrl = !isUrlValid(url);
    }
    this.setState({ invalidUrl, url, addInfo });

    if (changed && !invalidUrl) {
      this.debounceSaveScholarships();
    }
  };

  onScholarshipFormChange = (index, fieldName, value) => {
    const { scholarshipsConfig } = this.state;
    /* istanbul ignore else */
    if (scholarshipsConfig[index]) {
      const { name, awardType, awAmountMin, awAmountMax } = scholarshipsConfig[index].mbScholarship;
      if (fieldName ==='awardType' && value === 'RANGE' && Number(awAmountMax) === 0) {
        scholarshipsConfig[index].mbScholarship.awAmountMax = undefined;
      }
      const requiredFieldsMissing = checkRequiredFields({ name, awardType, awAmountMin, awAmountMax, [fieldName]: value });
      const { gpaMin, classRankMax, satMin, actMin } = scholarshipsConfig[index].mbScholarship;
      const atLeast1Requirement = requiredValuesExist({ gpaMin, classRankMax, satMin, actMin, [fieldName]: value });
      const errors = checkForErrors(this.props.intl, { ...scholarshipsConfig[index].mbScholarship, [fieldName]: value });

      this.setConfigState(index, {
        mbScholarship: {
          ...scholarshipsConfig[index].mbScholarship,
          [fieldName]: value,
        },
        formDirty: scholarshipsConfig[index].formDirty || scholarshipsConfig[index].mbScholarship[fieldName] !== value,
        errors: {
          ...errors,
          requiredFieldsMissing,
          atLeast1Requirement: atLeast1Requirement ? undefined : scholarshipsConfig[index].errors.atLeast1Requirement,
        },
      });
    }
  };

  onDelete = (index) => this.saveScholarship(index, true, false);

  onCancel = (index) => {
    const { scholarshipsConfig: initialConfig } = this.state.initialState;
    const { scholarshipsConfig, scholarshipsConfigKeys } = this.state;

    /* istanbul ignore else */
    if (scholarshipsConfig[index]) {
      if (scholarshipsConfig[index].isNew) {
        const changedConfig = {};
        const changedConfigKeys = [];
        scholarshipsConfigKeys.forEach((key) => {
          if (key !== index && !!scholarshipsConfig[key]) {
            changedConfig[key] = {
              ...scholarshipsConfig[key],
            };
            changedConfigKeys.push(key);
          }
        });
        this.setState({
          scholarshipsConfig: {
            ...changedConfig,
          },
          scholarshipsConfigKeys: changedConfigKeys,
        });
      } else {
        this.setConfigState(index, {
          formOpen: false,
          formDirty: false,
          errors: { requiredFieldsMissing: [] },
          forceShowErrors: false,
          mbScholarship: { ...initialConfig[index].mbScholarship },
        });
      }
    }
  };

  onSave = async (index) => {
    const { scholarshipsConfig } = this.state;
    const { intl } = this.props;
    /* istanbul ignore else */
    if (scholarshipsConfig[index]) {
      if (scholarshipsConfig[index].formDirty) {
        const errors = checkForErrors(intl, scholarshipsConfig[index].mbScholarship);
        const requiredFieldsMissing = checkRequiredFields(scholarshipsConfig[index].mbScholarship);
        if (this.checkForDuplicateNames(index, get(scholarshipsConfig[index], 'mbScholarship.name'))) {
          errors.duplicateName = intl.formatMessage(messages.duplicateName);
        }
        if (!errorsExist(errors) && requiredFieldsMissing.length <= 0) {
          await this.saveScholarship(index, false, false);
        } else {
          this.setConfigState(index, {
            errors: {
              ...errors,
              requiredFieldsMissing,
            },
            forceShowErrors: true,
            formDirty: false,
          });
        }
      }
    }
  };

  handleSaveAll = async () => {
    const { scholarshipsConfig } = this.state;
    const { intl } = this.props;
    let canSave = true;
    Object.keys(scholarshipsConfig).forEach((key) => {
      if (scholarshipsConfig[key].formDirty) {
        const errors = checkForErrors(intl, scholarshipsConfig[key].mbScholarship);
        const requiredFieldsMissing = checkRequiredFields(scholarshipsConfig[key].mbScholarship);
        if (this.checkForDuplicateNames(key, get(scholarshipsConfig[key], 'mbScholarship.name'))) {
          errors.duplicateName = intl.formatMessage(messages.duplicateName);
        }
        /* istanbul ignore else */
        if (errorsExist(errors) || requiredFieldsMissing.length > 0) {
          canSave = false;
          this.setConfigState(key, {
            errors: {
              ...errors,
              requiredFieldsMissing,
            },
            forceShowErrors: true,
          });
        }
      }
    });

    if (canSave === false) return Promise.reject(new Error('Errors exist'));

    const saveSucceeded = await this.saveScholarship(null, false, true);
    return saveSucceeded ? Promise.resolve() : Promise.reject(new Error('Save Failed'));
  };

  onCancelRetrySave = () => {
    this.setState({ saveFailureProps: null });
  };

  onConfirmRetrySave = () => {
    const { saveFailureProps } = this.state;
    /* istanbul ignore next */
    const { index, isDelete, saveAll } = saveFailureProps || {};
    this.saveScholarship(index, isDelete, saveAll);
  };

  getSaveFailureModal = () => {
    const { intl } = this.props;
    const { name } = this.state.saveFailureProps || {};
    return (
      <GenericModal
        open={!!this.state.saveFailureProps}
        title={intl.formatMessage(messages.saveFailureHeader, { scholarshipName: name })}
        message={intl.formatMessage(messages.saveFailure)}
        cancelText="CANCEL"
        confirmText="SAVE"
        onCancel={this.onCancelRetrySave}
        onConfirm={this.onConfirmRetrySave}
      />
    );
  };

  getUnsavedChangesModal = () => {
    const { scholarshipsConfig } = this.state;
    const isDirty = !!Object.keys(scholarshipsConfig).find((key) => scholarshipsConfig[key].formDirty === true);

    return (
      <UnsavedChangesPrompt
        when={isDirty}
        saveChanges={/* istanbul ignore next */ () => this.handleSaveAll()}
      />
    );
  };

  checkForDuplicateNames = (index, name) => {
    const { scholarshipsConfig } = this.state;
    return Object.keys(scholarshipsConfig).some((key) =>
      get(scholarshipsConfig[key], 'mbScholarship.name', '').toLowerCase() === name.toLowerCase() && index !== key);
  };

  getScholarshipsConfigsInOrder = () => {
    const { scholarshipsConfigKeys, scholarshipsConfig } = this.state;
    const configsList = [];
    scholarshipsConfigKeys.forEach((key) => {
      if (scholarshipsConfig[key]) {
        configsList.push(scholarshipsConfig[key]);
      }
    });

    return configsList;
  }

  render() {
    const { invalidUrl, url, addInfo, stateInitialized, currentFocusIndex } = this.state;
    const scholarshipsConfigsList = this.getScholarshipsConfigsInOrder();

    return (
      <Item
        styleName="scholarships-container"
        data-cy="scholarships-container"
      >
        <PageTitle subTitle="Merit Scholarships" title="Scholarships" category="Intersect" />
        <Header as="h1" color="black" size="large" id="pageTitle" data-cy="scholarships-heading-title" styleName="scholarships-title">
          <FormattedMessage {...messages.scholarshipsTitle} />
        </Header>
        <div styleName="scholarships-heading-subtitle space" data-cy="scholarships-heading-subtitle">
          <FormattedMessage {...messages.scholarshipsSubText} />
        </div>
        <Grid stackable width={16} verticalAlign="middle" styleName="merit-scholarships-content">
          <SubHeader />
          <ScholarshipsInfo url={url} addInfo={addInfo} invalidUrl={invalidUrl} onChange={this.onScholarshipInfoChange} />
          <Divider styleName="menu-divider" />
          <Accordion styleName="merit-scholarships-form-accordion">
            {scholarshipsConfigsList.length > 0 && scholarshipsConfigsList.map((config) => (
              <ScholarshipForm
                key={`scholarship-form-${config.index}`}
                config={pick(config, ['index', 'formOpen', 'formDirty', 'errors', 'forceShowErrors', 'isNew'])}
                isFocus={currentFocusIndex === config.index}
                scholarship={config.mbScholarship}
                toggleForm={this.onToggleForm}
                onChange={this.onScholarshipFormChange}
                onCancel={this.onCancel}
                onDelete={this.onDelete}
                onSave={this.onSave}
                checkForDuplicateNames={this.checkForDuplicateNames}
              />
            ))}
          </Accordion>
          {this.getSaveFailureModal()}
          {this.getUnsavedChangesModal()}
          <Loader active={!stateInitialized} />
          <Grid.Row>
            <Button
              key="scholarships-add-new-scholarship"
              styleName="add-new-scholarships-btn"
              data-cy="scholarships-button-add-new-scholarship"
              aria-label="Click to add a new scholarship"
              title="Add New Scholarship"
              onClick={this.handleAddScholarship}
              disabled={!stateInitialized}
            >
              <Icon name="plus" />
              <FormattedMessage {...messages.addNewScholarship} />
            </Button>
          </Grid.Row>
        </Grid>
      </Item>
    );
  }
}

export default injectIntl(Scholarships);
